import type { Ou } from '../api/types';
import { nChoses } from './accords';

/**
 * Les mots de la journée : la question avant de planifier un jour, « Propose-moi une journée »,
 * « Je choisis moi-même », la journée en cours, « Chercher une tâche » et « Préparer ma
 * journée ».
 */

/* La journée au téléphone : le titre de l'onglet Journée (chantier `telephone-cadre`, ticket 01). */
export const MA_JOURNEE = 'Ma journée';
export const TA_JOURNEE = 'Ta journée';
export const VOIR = 'Voir';
export const A_VERIFIER = 'À vérifier';

/*
 * La question avant de planifier un jour, au téléphone (chantier `telephone-journee-agenda`,
 * ticket 01, décisions d'Igor du 2026-10-05, 7, 8 et 20 ; maquette `design/telephone.html`,
 * piste « Journée », « La question »).
 */
export const RIEN_DE_PREVU_AUJOURDHUI = 'Rien n’est prévu aujourd’hui.';
export const RIEN_DE_PREVU_CE_JOUR = 'Rien n’est prévu ce jour-là.';
export const PLANIFIER_CE_JOUR = 'Planifier ce jour';
export const COMBIEN_DE_TEMPS = 'Combien de temps as-tu pour tes projets ?';
export const ET_TU_ES_PLUTOT = 'Et tu es plutôt…';
export const DES_QUELLE_HEURE = 'Dès quelle heure';
export const OU_SERAS_TU = 'Où seras-tu';
/** Les temps de la question, dans l'ordre (décision 20) : un sous-ensemble de `TEMPS_PROPOSES`. */
export const MOTS_TEMPS_DE_LA_QUESTION: [number, string][] = [
  [60, '1 h'],
  [120, '2 h'],
  [240, '½ journée'],
  [420, 'Journée'],
];
export const PROPOSE_MOI = 'Propose-moi une journée';
export const PROPOSE_MOI_NOTE = 'L’app la remplit, tu corriges';
export const JE_CHOISIS = 'Je choisis moi-même';
export const JE_CHOISIS_NOTE = 'Tu ajoutes les tâches une à une';
export const LA_QUESTION = 'La question';
export const JE_TE_PROPOSE = 'Je te propose';
/*
 * « Propose-moi une journée » (chantier `telephone-journee-agenda`, ticket 02, décision 9 ;
 * maquette `design/telephone.html`, piste « Journée », « Je te propose ») : une possibilité,
 * jamais imposée ; × enlève, ↻ remplace, « + Ajouter une tâche », « C'est ma journée ».
 */
export const GESTES_DE_LA_PROPOSITION = '× enlève la tâche. ↻ la remplace par une autre qui tient dans le même temps.';
export const AJOUTER_UNE_TACHE = '+ Ajouter une tâche';
export const REPLIER_LA_LISTE = 'Replier la liste';
export const RIEN_NE_TIENT_ICI = 'Rien ne tient dans ce temps avec ces réponses. Ajoute une tâche, ou reviens à la question.';
export const RIEN_A_AJOUTER = 'Plus rien à ajouter ce jour-là, avec ces réponses.';
export const RIEN_QUI_TIENNE = 'Rien d’autre ne tient à sa place : elle reste.';
export const MOT_DEBLOQUANT = 'débloquant';
export const MOT_PRIORITAIRE = 'prioritaire';
export const MOT_NE_RENTRE_PLUS = 'ne rentre plus';
/*
 * « Je choisis moi-même » (chantier `telephone-journee-agenda`, ticket 03, décision 10 ; maquette
 * `design/telephone.html`, piste « Journée », « Je choisis ») : trois boutons, le projet, le temps,
 * le courage, chacun ouvre sa liste dans une feuille du bas ; la journée se remplit en haut.
 */
export const JE_CHOISIS_TITRE = 'Je choisis';
export const DANS_TA_JOURNEE = 'Dans ta journée';
export const QUEL_PROJET = 'Quel projet ?';
export const QUELLE_DUREE = 'Quelle durée ?';
export const QUEL_COURAGE = 'Quel courage ?';
/** Les crans du temps de « Je choisis » : jusqu'à 10 min, 30 min, 1 h, plus d'une heure, tout. */
export const MOTS_CRANS_DE_TEMPS: Record<'dix_min' | 'trente_min' | 'une_heure' | 'plus_long' | 'tout', string> = {
  dix_min: '10 min',
  trente_min: '30 min',
  une_heure: '1 h',
  plus_long: 'Plus long',
  tout: 'Toute durée',
};
export const RIEN_AVEC_CES_BOUTONS = 'Rien ne répond à ces trois boutons. Change-les, ou cherche une autre tâche.';
/** « 22 possibles » sous un choix de la feuille : ce qui répond aux autres boutons ; rien se dit. */
export function nombreDePossibles(combien: number): string {
  if (combien === 0) return 'Rien avec ces réglages';
  return `${combien} ${combien > 1 ? 'possibles' : 'possible'}`;
}
/** Le nom d'un des trois boutons pour le lecteur d'écran : « Projet : Maison ». */
export function boutonDeJeChoisis(nom: 'Projet' | 'Temps' | 'Courage', choix: string): string {
  return `${nom} : ${choix}`;
}
/*
 * La journée en cours au téléphone (chantier `telephone-journee-agenda`, ticket 04, décisions
 * d'Igor du 2026-10-05, 7, 8, 11 et 20 ; maquette `design/telephone.html`, piste « Journée »,
 * « La journée en cours ») : la tâche de l'heure en grand, « Plus tard » à deux choix,
 * « Annuler » après une coche, « Tout est fait », « Changer ma journée » qui la rouvre.
 */
export const CHANGER_MA_JOURNEE = 'Changer ma journée';
export const PLUS_TARD = 'Plus tard';
export const PLUS_TARD_AUJOURDHUI = 'Plus tard aujourd’hui';
export const PLUS_TARD_AUJOURDHUI_NOTE = 'En fin de journée, les heures suivent';
export const PAS_AUJOURDHUI = 'Pas aujourd’hui';
export const PAS_AUJOURDHUI_NOTE = 'Elle sort de ta journée, libre dans son projet';
export const TOUT_EST_FAIT = 'Tout est fait.';
export const RECOMMENCER = 'Recommencer';
export const ON_RECOMMENCE = 'On reprend ta journée : ce qui est fait reste. Rien ne change avant « C’est ma journée ».';
export const GESTES_DU_CHANGEMENT = '× enlève la tâche. Touche une heure pour la changer.';
export const RECOMMENCER_NOTE = 'Repose la question, rien ne change avant « C’est ma journée »';
/** Ce que dit l'étiquette de la tâche de l'heure : en cours, la prochaine, ou restée d'avant. */
export const MOTS_DE_L_HEURE: Record<'en_ce_moment' | 'a_suivre' | 'a_faire', string> = {
  en_ce_moment: 'En ce moment',
  a_suivre: 'À suivre',
  a_faire: 'Encore à faire',
};
/** « 1 sur 3 faites · 1 h 20 prévues ». */
export function faitesEtPrevues(faites: number, total: number, duree: string): string {
  return `${faites} sur ${total} ${total > 1 ? 'faites' : 'faite'} · ${duree} prévues`;
}
export function passeEnFinDeJournee(titre: string): string {
  return `« ${titre} » passe en fin de journée.`;
}
export function sortDeTaJournee(titre: string): string {
  return `« ${titre} » sort de ta journée, libre dans son projet.`;
}
export function remiseAFaire(titre: string): string {
  return `« ${titre} » est remise à faire.`;
}
export function plusTardPour(titre: string): string {
  return `Plus tard : ${titre}`;
}
export function cEstFaitPour(titre: string): string {
  return `C’est fait : ${titre}`;
}
export function remettreAFaire(titre: string): string {
  return `Remettre à faire ${titre}`;
}
export function remplacerDansLaJournee(titre: string): string {
  return `Remplacer ${titre}`;
}
/** « libère 2 choses », sur la ligne d'une tâche proposée. */
export function libereDesChoses(combien: number): string {
  return `libère ${nChoses(combien)}`;
}
/** « échéance le mardi 13 octobre ». */
export function echeanceLe(jour: string): string {
  return `échéance le ${jour}`;
}
/** « 15 min, durée non posée » : la durée prise faute d'en avoir une. */
export function dureeNonPosee(duree: string): string {
  return `${duree}, durée non posée`;
}
/** Un refus de « C'est ma journée » : des tâches qui ne se font plus ce jour-là, nommées. */
export function pasPossibleCeJour(titres: string[]): string {
  const noms = titres.map((t) => `« ${t} »`).join(', ');
  return titres.length > 1
    ? `${noms} ne peuvent plus se faire ce jour-là : enlève-les, puis réessaie.`
    : `${noms} ne peut plus se faire ce jour-là : enlève-la, puis réessaie.`;
}
/** Un refus de « C'est ma journée » : une tâche déjà prévue un autre jour, nommée avec ce jour. */
export function prevueUnAutreJour(titre: string, jour: string): string {
  return `« ${titre} » est déjà prévue le ${jour} : enlève-la d’ici, ou retire-la de ce jour-là.`;
}
export const PAS_DE_CONNEXION_RIEN_D_ENREGISTRE = 'Pas de connexion : rien n’est enregistré.';
export const RIEN_N_EST_ENREGISTRE = 'Ça n’a pas marché : rien n’est enregistré.';
/**
 * « C'est ma journée » refusé : la journée a été changée sur un autre appareil depuis qu'on l'a
 * ouverte (chantier nettoyage-regles, ticket 04). Elle est relue ; les choix à l'écran restent,
 * et un second appui les enregistre.
 */
export const JOURNEE_CHANGEE_AILLEURS =
  'Ta journée a changé ailleurs, rien n’est enregistré. Je l’ai relue : touche encore « C’est ma journée » pour garder celle-ci.';
/*
 * Les refus des bandeaux de la veille et du lendemain, dans « Maintenant » : leurs mots à eux,
 * gardés tels quels même s'ils diffèrent de ceux de la journée qu'on compose (chantier
 * `nettoyage-mots`, ticket 05, choix de Claude, Igor absent).
 */
export const BANDEAU_PAS_DE_CONNEXION = 'Pas de connexion : rien n’a changé.';
export const BANDEAU_FINIRAIT_APRES_MINUIT = 'Ça finirait après minuit ce jour-là : choisis un autre jour.';
export const BANDEAU_JOUR_INVALIDE = 'Choisis aujourd’hui ou un jour à venir, un autre que celui-ci.';
export const BANDEAU_RIEN_N_A_CHANGE = 'Ça n’a pas marché : rien n’a changé.';

/*
 * « Chercher une tâche » dans « Préparer ma journée » : les trois tris (ticket 03 de telephone),
 * et la phrase qui dit leur ordre. Le premier suit l'ordre du serveur, celui de la flottaison
 * (chantier `flottaison-suite`, ticket 05 ; renommé au chantier `revue-design`, ticket 06).
 */
export const CE_QUI_FLOTTE = 'Ce qui flotte';
export const PAR_PROJET = 'Par projet';
export const LES_PLUS_RAPIDES = 'Les plus rapides';
export const ORDRE_CE_QUI_FLOTTE = 'Ce qui flotte le plus haut d’abord, comme dans « Maintenant ».';
export const ORDRE_PAR_PROJET = 'Tes projets, tu ouvres celui que tu veux avancer.';
export const ORDRE_LES_PLUS_RAPIDES = 'Les plus courtes d’abord, pour caser beaucoup de petites choses.';
export const SANS_PROJET = 'Sans projet';
export const TRIER_CE_QUE_TU_PEUX_FAIRE = 'Trier ce que tu peux faire';
export const FAITE = 'Faite';
/*
 * « Préparer ma journée » refait d'après sa maquette (chantier `revue-design`, ticket 06,
 * `design/agenda.html`) : un seul panneau, chaque tâche à son heure, fixée en la touchant ou à
 * la suite, le temps libre entre deux, ce qu'on peut ajouter, et « C'est ma journée ».
 */
export const PREPARER_MA_JOURNEE = 'Préparer ma journée';
/** La tête de la liste d'une journée encore vide : rien n'est proposé d'avance (Igor, 2026-10-04, B). */
export const AJOUTE_CE_QUE_TU_VEUX = 'Ajoute ce que tu veux faire ce jour-là';
export const TOUCHE_UNE_HEURE = 'touche une heure pour la changer';
export const FIXEE = 'fixée';
export const A_LA_SUITE = 'à la suite';
export const REMETTRE_A_LA_SUITE = 'Remettre à la suite';
export const A_AJOUTER = 'À ajouter';
export const CHERCHER_UNE_TACHE = 'Chercher une tâche';
export const REPLIER_LA_RECHERCHE = 'Replier la recherche';
export const C_EST_MA_JOURNEE = 'C’est ma journée';
export const IMPOSE = 'Imposé';
export const APRES_MINUIT = 'Après minuit';
export const MAINTENANT_DEBUT = 'Maintenant';
export const OU_A = 'Ou à';
export const RIEN_A_PROPOSER = 'Rien à prévoir pour l’instant, avec ce « où ».';
export const RIEN_NE_CORRESPOND = 'Aucune tâche ne correspond.';
export const TEMPS_REMPLI = 'Ton temps est rempli.';
export const NE_RENTRE_PLUS = 'Ça ne rentre plus dans ton temps ce jour-là. Retire une autre tâche, ou donne-toi plus de temps.';
export const FINIRAIT_APRES_MINUIT = 'Ta journée finirait après minuit : retire une tâche, ou commence plus tôt.';
export const JOUR_PASSE = 'Ce jour est passé : il ne se prépare plus.';
export const JOURNEE_PRETE = 'Ta journée est prête.';
export const DEFAIRE_CETTE_JOURNEE = 'Défaire cette journée';
export const DEFAIRE_NOTE = 'Ses tâches redeviennent libres, rien d’autre ne change.';
export const OUI_DEFAIRE = 'Oui, défaire cette journée';
export const NON_LA_GARDER = 'Non, la garder';
/** « Ta journée du vendredi 2 octobre est prête. » */
export function journeePreteDu(jour: string): string {
  return `Ta journée du ${jour} est prête.`;
}
export function journeeDefaite(jour: string): string {
  return `La journée du ${jour} est défaite. Ses tâches redeviennent libres.`;
}
/** « Échéance · Le prévisionnel », dans une case du Calendrier et en tête du panneau. */
export function echeanceDe(titre: string): string {
  return `Échéance · ${titre}`;
}
/** Le temps qu'on se donne, dans l'ordre du menu ; ce que le serveur accepte (`TEMPS_PROPOSES`). */
export const MOTS_TEMPS_DU_JOUR: [number, string][] = [
  [30, '30 min'],
  [60, '1 h'],
  [120, '2 h'],
  [180, '3 h'],
  [240, 'Une demi-journée'],
  [420, 'Toute la journée'],
];
/** Les trois « où » du panneau, comme la maquette : « Chez moi ou dehors ». */
export const MOTS_OU_DU_JOUR: Record<Ou, string> = {
  tout: 'Chez moi ou dehors',
  sans_bouger: 'Chez moi',
  il_faut_y_aller: 'Dehors',
};
/** « Dès 9 h 30 » : l'heure où la journée commence, sur sa pastille. */
export function desHeure(heure: string): string {
  return `Dès ${heure}`;
}
/** « libre 1 h 30 » entre deux choses de la journée. */
export function libreDurant(duree: string): string {
  return `libre ${duree}`;
}
/** « 3 heures fixées, 1 à la suite » sous la jauge ; ce qui vaut zéro se tait. */
export function heuresFixeesEtALaSuite(fixees: number, aLaSuite: number): string {
  const morceaux = [
    fixees > 0 ? `${fixees} ${fixees > 1 ? 'heures fixées' : 'heure fixée'}` : null,
    aLaSuite > 0 ? `${aLaSuite} ${A_LA_SUITE}` : null,
  ];
  return morceaux.filter(Boolean).join(', ');
}
/** « 41 tâches trop longues pour ce qui reste (30 min). » */
export function tropLonguesPourCeQuiReste(combien: number, reste: string): string {
  return `${combien} ${combien > 1 ? 'tâches trop longues' : 'tâche trop longue'} pour ce qui reste (${reste}).`;
}
/** Une heure fixée qui tomberait sur autre chose : le refus du serveur, dit en clair. */
export function heureQuiChevauche(tache: string, heure: string, avec: string, sonHeure: string): string {
  return `« ${tache} » à ${heure} tomberait sur « ${avec} » (${sonHeure}) : choisis une autre heure.`;
}
export function changerLHeureDe(titre: string): string {
  return `Changer l’heure de ${titre}`;
}
export function retirerDeLaJournee(titre: string): string {
  return `Retirer ${titre}`;
}
export function ajouterALaJournee(titre: string): string {
  return `Ajouter ${titre}`;
}
export function cocherDansLaJournee(titre: string, fait: boolean): string {
  return fait ? `Décocher ${titre}` : `Cocher ${titre}`;
}
